// ============================================================================
// esign module — client for the `esign` Edge Function (Phase 2).
// Public (tokenized) calls carry no Authorization; internal calls attach the
// bridged Supabase JWT. The function is deployed verify_jwt=false and does
// its own auth (see supabase/functions/esign/index.ts).
// ============================================================================

import { SUPABASE_ANON_KEY, getSupabaseToken } from '../../shared/supabase';

const ESIGN_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/esign`;

export interface DeliveryResult {
  delivered: boolean;
  channel: string;
  reason?: string;
}

export interface SignerMeta {
  state: 'active' | 'verified' | 'completed' | 'expired';
  contract_number: string | null;
  account_name: string | null;
  facility_name: string | null;
  signer_email_masked: string;
  signer_name: string | null;
  channel: string | null;
  verified: boolean;
  otp_pending: boolean;
}

export interface SignerTokenStatus {
  token_id: string;
  signer_email: string;
  signer_name: string | null;
  channel: string | null;
  created_at: string;
  expires_at: string;
  verified_at: string | null;
  consumed_at: string | null;
  otp_attempts: number;
}

export class EsignError extends Error {
  status: number;
  code: string;
  constructor(status: number, code: string, message?: string) {
    super(message ?? code);
    this.status = status;
    this.code = code;
  }
}

async function call<T>(
  body: Record<string, unknown>,
  internal: boolean,
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    apikey: SUPABASE_ANON_KEY,
  };
  if (internal) {
    const jwt = getSupabaseToken();
    if (!jwt) throw new EsignError(401, 'not_signed_in');
    headers.Authorization = `Bearer ${jwt}`;
  }
  const res = await fetch(ESIGN_URL, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  const json = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    throw new EsignError(
      res.status,
      String(json.error ?? `http_${res.status}`),
      json.message ? String(json.message) : undefined,
    );
  }
  return json as T;
}

export function esignPublic<T>(body: Record<string, unknown>): Promise<T> {
  return call<T>(body, false);
}

export function esignInternal<T>(body: Record<string, unknown>): Promise<T> {
  return call<T>(body, true);
}

/** Fetch the contract PDF for a signing token as an object URL. */
export async function fetchSigningPdf(token: string): Promise<string> {
  const res = await fetch(ESIGN_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', apikey: SUPABASE_ANON_KEY },
    body: JSON.stringify({ action: 'pdf', token }),
  });
  if (!res.ok) {
    const json = (await res.json().catch(() => ({}))) as { error?: string };
    throw new EsignError(res.status, json.error ?? 'pdf_fetch_failed');
  }
  return URL.createObjectURL(await res.blob());
}
